/**
 * The console's strings, the panel's way: every one is a tuple `[en, zh]`, so
 * the compiler demands both languages and the parity test catches what the
 * compiler cannot see.
 *
 * The locale comes from the same cookie the directory and the panel write, so
 * an operator who reads the panel in Chinese reads this in Chinese too.
 */

import { createContext, type ReactNode, useContext, useMemo } from "react";

export const LOCALES = ["en", "zh"] as const;
export type Locale = (typeof LOCALES)[number];
export const LOCALE_COOKIE = "ONDIT_LOCALE";

type Vars = Record<string, string | number>;

export const STRINGS: Record<string, [string, string]> = {
	"reconcile.title": ["Model reconciliation", "模型调用对账"],
	"reconcile.refresh": ["Refresh", "刷新"],
	"reconcile.call": ["Model / request", "模型 / 请求"],
	"reconcile.brand": ["Account / brand", "账户 / 品牌"],
	"reconcile.status": ["Status", "状态"],
	"reconcile.reserved": ["Reserved credits", "预留积分"],
	"reconcile.inspect": ["Review evidence", "核对凭证"],
	"reconcile.uncertain": ["Outcome unknown", "结果不确定"],
	"reconcile.received": ["Receipt awaiting settlement", "回执待结算"],
	"reconcile.empty": ["No calls await reconciliation", "没有待对账的调用"],
	"reconcile.previous": ["Previous", "上一页"],
	"reconcile.next": ["Next", "下一页"],
	"reconcile.original": ["Original call and reconciliation record", "原始调用与对账记录"],
	"reconcile.reference": ["Gateway receipt or support case reference", "网关回执或工单编号"],
	"reconcile.note": ["Verified evidence and conclusion", "已核实的依据与结论"],
	"reconcile.decision": ["Resolution", "处理方式"],
	"reconcile.receipt": ["Settle verified receipt", "按已核实回执结算"],
	"reconcile.notBilled": ["Confirmed not billed", "确认未计费"],
	"reconcile.receiptJson": ["Verified response (JSON)", "已核实响应（JSON）"],
	"reconcile.apply": ["Record reconciliation", "记录对账结果"],
	"reconcile.evidenceRule": [
		"Check the exact request with the gateway. Supply its receipt or an explicit confirmation that it was not billed. Missing usage is not zero usage.",
		"按原请求向网关核实，提供真实回执或明确的未计费确认。缺失用量不等于零用量。",
	],
	"reconcile.receiptShape": [
		"Required: model, text, citations, web_queries, raw and usage (input_tokens, output_tokens, cache_read_tokens, cache_write_tokens). Copilot also needs raw.content and raw.stop_reason.",
		"必填：model、text、citations、web_queries、raw、usage（input_tokens、output_tokens、cache_read_tokens、cache_write_tokens）。Copilot 还需 raw.content 和 raw.stop_reason。",
	],
	"reconcile.chargeConsequence": [
		"Records the evidence and charges measured usage once at the frozen rates, within the reservation. Does not resume the workflow. This decision cannot be replaced.",
		"留存凭证，按冻结价格在预留额度内结算一次真实用量。不会恢复工作流。提交后不能替换此决定。",
	],
	"reconcile.close": ["Close", "关闭"],
	"reconcile.releaseConsequence": [
		"Releases the reservation without charging or resending the request. Requires confirmed absence of a gateway charge. This decision cannot be replaced.",
		"释放预留额度，不扣款，也不重发请求。必须已确认网关未计费。提交后不能替换此决定。",
	],
	// -- shell
	"app.name": ["Console", "管理中心"],
	"nav.skipContent": ["Skip to content", "跳转到主要内容"],
	"nav.overview": ["Overview", "总览"],
	"nav.citations": ["Citation gate", "引用达标"],
	"nav.usage": ["Model spend", "模型开销"],
	"nav.users": ["People", "用户"],
	"nav.orgs": ["Accounts", "客户账户"],
	"nav.entities": ["Listings", "收录条目"],
	"nav.submissions": ["Submissions", "收录申请"],
	"nav.pages": ["Pages", "页面"],
	"nav.jobs": ["Jobs", "任务队列"],
	"nav.plans": ["Plans and prices", "套餐与定价"],
	"nav.catalogue": ["Industries", "行业与分类"],
	"nav.audit": ["Audit log", "操作日志"],
	"nav.signOut": ["Sign out", "退出登录"],
	"nav.language": ["Language", "语言"],
	"nav.themeSystem": ["System", "跟随系统"],
	"nav.themeLight": ["Light", "浅色"],
	"nav.themeDark": ["Dark", "深色"],
	"nav.theme": ["Theme", "外观"],
	"nav.group.measure": ["Measure", "度量"],
	"nav.group.customers": ["Customers", "客户"],
	"nav.group.directory": ["Directory", "目录"],
	"nav.group.operations": ["Operations", "运维"],
	"nav.group.catalogue": ["Catalogue", "配置"],
	"nav.openPanel": ["Open the panel", "打开客户面板"],
	"nav.openSite": ["Open the directory", "打开目录站"],

	// -- sign in
	"login.title": ["Operator sign-in", "管理员登录"],
	"login.lede": [
		"This is the operator console for Ondit. Every change made here is recorded against your account.",
		"这是 Ondit 的管理中心。这里的每一次操作都会记录在你的账号名下。",
	],
	"login.email": ["Email", "邮箱"],
	"login.password": ["Password", "密码"],
	"login.submit": ["Sign in", "登录"],
	"login.failed": [
		"That address and password do not match an operator account.",
		"邮箱或密码不正确，或该账号不是管理员。",
	],

	// -- states
	"state.loading": ["Loading", "加载中"],
	"state.loadingSlow": ["Still loading. The page will appear when ready.", "仍在加载，完成后会自动显示。"],
	"state.error": ["Something went wrong", "出错了"],
	"state.retry": ["Try again", "重试"],
	"state.empty": ["Nothing here yet", "暂无内容"],
	"state.saved": ["Saved", "已保存"],
	"state.confirm": ["Confirm", "确认"],
	"state.cancel": ["Cancel", "取消"],
	"state.save": ["Save", "保存"],
	"state.search": ["Search", "搜索"],
	"state.all": ["All", "全部"],
	"state.none": ["None", "无"],
	"state.yes": ["Yes", "是"],
	"state.no": ["No", "否"],
	"state.more": ["Load more", "加载更多"],
	"state.of": ["{shown} of {total}", "{shown} / {total}"],
	"state.shown": ["{shown} shown", "已显示 {shown} 条"],
	"state.never": ["never", "从未"],
	"state.note": ["Note", "备注"],
	"state.noteHint": ["Kept in the audit log, never shown to the customer.", "只记录在操作日志里，不会展示给客户。"],
	"state.open": ["Open", "打开"],
	"state.close": ["Close", "收起"],
	"state.id": ["ID", "ID"],
	"state.created": ["Created", "创建于"],
	"state.updated": ["Updated", "更新于"],
	"state.status": ["Status", "状态"],
	"state.actions": ["Actions", "操作"],
	"state.moreActions": ["More actions", "更多操作"],

	// -- overview
	"overview.title": ["Overview", "总览"],
	"overview.lede": [
		"Is the product working, and is it costing what it should.",
		"产品是否正常运转，开销是否在预期之内。",
	],
	"overview.week": ["Week {week}", "第 {week} 周"],
	"overview.users": ["People", "用户"],
	"overview.usersVerified": ["{n} verified", "{n} 已验证"],
	"overview.usersNew": ["{n} new this week", "本周新增 {n}"],
	"overview.orgs": ["Accounts", "客户账户"],
	"overview.orgsPaid": ["{n} paying", "{n} 付费中"],
	"overview.orgsPaused": ["{n} paused", "{n} 已暂停"],
	"overview.entities": ["Listings", "收录条目"],
	"overview.entitiesClaimed": ["{n} claimed", "{n} 已认领"],
	"overview.pages": ["Pages", "页面"],
	"overview.pagesReview": ["{n} waiting for review", "{n} 待审核"],
	"overview.submissions": ["Submissions waiting", "待处理的收录申请"],
	"overview.jobs": ["Queue", "任务队列"],
	"overview.jobsQueued": ["{n} queued", "{n} 排队中"],
	"overview.jobsRunning": ["{n} running", "{n} 运行中"],
	"overview.jobsFailed": ["{n} failed", "{n} 失败"],
	"overview.samples": ["Answers sampled, 24h", "24 小时采样答案"],
	"overview.samplesNotices": ["{n} gateway refusals", "{n} 次网关拒绝"],
	"overview.spendToday": ["Platform spend today", "平台今日开销"],
	"overview.spendCap": ["cap {cap}", "上限 {cap}"],
	"overview.spend30": ["Cost vs billed, 30 days", "30 天成本 vs 计费"],
	"overview.agents": ["Agent schedules", "智能体计划"],
	"overview.agentsRunning": ["{n} running now", "{n} 正在运行"],
	"overview.config": ["Configuration", "配置状态"],
	"overview.configLede": ["What this deployment has and has not been given.", "这次部署配了什么、缺什么。"],
	"config.paygate": ["paygate (billing)", "paygate（收款）"],
	"config.platform_key": ["Platform gateway key", "平台网关密钥"],
	"config.mail": ["Transactional mail", "邮件发送"],
	"config.google": ["Sign in with Google", "Google 登录"],
	"config.github": ["Sign in with GitHub", "GitHub 登录"],
	"config.revalidate": ["Site revalidation", "站点缓存刷新"],
	"config.on": ["configured", "已配置"],
	"config.off": ["not configured", "未配置"],
	"config.origins": ["Origins", "域名"],
	"config.caps": ["Daily caps", "每日上限"],
	"config.platformCap": ["platform {amount}", "平台 {amount}"],
	"config.concurrent": ["{n} concurrent gateway calls", "网关并发 {n}"],

	// -- citation gate
	"citations.title": ["Citation gate", "引用达标"],
	"citations.lede": [
		"Is the directory itself being cited. An engine passes when a fifth of its questions cite an Ondit page in a majority of replications; the gate is three engines.",
		"目录本身是否被引用。一个引擎在五分之一的问题上、多数重复采样中引用了 Ondit 页面即算通过；达标线是三个引擎。",
	],
	"citations.passing": ["{n} of {total} engines passing", "{total} 个引擎中 {n} 个通过"],
	"citations.needed": ["The gate is {n}.", "达标线是 {n} 个。"],
	"citations.engine": ["Engine", "引擎"],
	"citations.questionsPassing": ["Questions passing", "通过的问题"],
	"citations.questions": ["Questions", "问题数"],
	"citations.rate": ["Rate", "比例"],
	"citations.raw": ["Raw citations this week", "本周原始引用"],
	"citations.cited": ["Citations of Ondit", "引用 Ondit 次数"],
	"citations.byKind": ["Which page types get cited, 7 days", "7 天内被引用的页面类型"],
	"citations.kind": ["Kind", "类型"],
	"citations.week": ["Week", "周"],
	"citations.noData": ["No samples in this week.", "这一周没有采样。"],

	// -- usage
	"usage.title": ["Model spend", "模型开销"],
	"usage.lede": [
		"Thirty days of gateway calls. Cost is what left the platform account; billed is what customers were charged at list price.",
		"30 天的网关调用。成本是平台账户实际支出；计费是按牌价向客户收取的金额。",
	],
	"usage.byDay": ["By day", "按天"],
	"usage.byModel": ["By model", "按模型"],
	"usage.byKind": ["By kind of work", "按用途"],
	"usage.topOrgs": ["Accounts billed most", "计费最多的账户"],
	"usage.day": ["Day", "日期"],
	"usage.calls": ["Calls", "调用数"],
	"usage.cost": ["Cost", "成本"],
	"usage.billed": ["Billed", "计费"],
	"usage.model": ["Model", "模型"],
	"usage.tokens": ["Tokens in / out", "输入 / 输出 token"],
	"usage.kind": ["Kind", "用途"],

	// -- users
	"users.title": ["People", "用户"],
	"users.lede": ["Everyone with an account, operators included.", "所有注册账号，包括管理员。"],
	"users.email": ["Email", "邮箱"],
	"users.name": ["Name", "姓名"],
	"users.orgs": ["Accounts", "所属账户"],
	"users.verified": ["Verified", "已验证"],
	"users.unverified": ["Unverified", "未验证"],
	"users.admin": ["Operator", "管理员"],
	"users.password": ["Password set", "已设密码"],
	"users.providers": ["Sign-in", "登录方式"],
	"users.sessions": ["Sessions", "会话"],
	"users.filter.admins": ["Operators", "管理员"],
	"users.filter.unverified": ["Unverified", "未验证"],
	"users.verify": ["Mark verified", "标记为已验证"],
	"users.promote": ["Make an operator", "设为管理员"],
	"users.demote": ["Remove operator access", "取消管理员"],
	"users.demoteConfirm": [
		"Remove operator access from {email}? Their console sessions end now.",
		"取消 {email} 的管理员权限？其管理中心会话会立即结束。",
	],
	"users.promoteConfirm": [
		"Make {email} an operator? They will be able to do everything you can here.",
		"将 {email} 设为管理员？他们将拥有和你一样的权限。",
	],
	"users.revoke": ["Sign out everywhere", "全部登出"],
	"users.revokeConfirm": [
		"End every panel and console session for {email}?",
		"结束 {email} 的所有面板与管理中心会话？",
	],
	"users.revoked": [
		"{panel} panel and {console} console sessions ended.",
		"已结束 {panel} 个面板会话和 {console} 个管理中心会话。",
	],
	"users.identities": ["Linked sign-ins", "关联登录"],
	"users.actions": ["Recent actions", "最近操作"],
	"users.consoleSessions": ["{n} console sessions", "{n} 个管理中心会话"],
	"users.bootstrap": ["Bootstrap operator, set by the environment.", "由环境变量设定的初始管理员。"],
	"users.resetPassword": ["Reset the password", "重置密码"],
	"users.resetPasswordHint": [
		"For a customer whose reset mail never arrives. Ends their panel sessions and confirms the address. The password is not recorded anywhere.",
		"用于收不到重置邮件的客户。会结束其面板会话并标记邮箱已验证。密码本身不会被记录。",
	],
	"users.newPassword": ["New password", "新密码"],
	"users.generate": ["Generate", "随机生成"],
	"users.passwordSet": [
		"Password set. {n} panel sessions ended. Send it to the person yourself.",
		"密码已设置，结束了 {n} 个面板会话。请自行把密码告知对方。",
	],
	"users.bootstrapPassword": [
		"Set by ONDIT_ADMIN_PASSWORD in the environment.",
		"由环境变量 ONDIT_ADMIN_PASSWORD 设定。",
	],
	"users.noOrgs": [
		"This person is in no account, so there is nothing to grant to.",
		"该用户不属于任何账户，没有可操作的对象。",
	],
	"users.forAccount": ["Account", "账户"],
	"users.credits": ["Adjust credits", "调整积分"],
	"users.creditsHint": [
		"Credits belong to the account, not the person. A positive number grants, a negative one revokes; a revoke past the balance is refused.",
		"积分属于账户而非个人。正数为赠送，负数为扣回；超出余额的扣回会被拒绝。",
	],
	"users.creditsApply": ["Apply", "执行"],
	"users.level": ["Membership level", "会员级别"],

	// -- orgs
	"orgs.title": ["Accounts", "客户账户"],
	"orgs.lede": ["Every workspace: its plan, its balance, its people.", "每个工作区：套餐、余额、成员。"],
	"orgs.name": ["Account", "账户"],
	"orgs.plan": ["Plan", "套餐"],
	"orgs.subscription": ["Subscription", "订阅"],
	"orgs.credits": ["Credits", "积分"],
	"orgs.entities": ["Brands", "品牌"],
	"orgs.members": ["Members", "成员"],
	"orgs.spent30": ["Billed, 30d", "30 天计费"],
	"orgs.paused": ["Paused", "已暂停"],
	"orgs.filter.paid": ["Paying", "付费中"],
	"orgs.filter.paused": ["Paused", "已暂停"],
	"orgs.filter.expired": ["Lapsed", "已过期"],
	"orgs.state.none": ["Free listing", "免费收录"],
	"orgs.state.active": ["Active", "生效中"],
	"orgs.state.expired": ["Lapsed — read only", "已过期，只读"],
	"orgs.pause": ["Pause the account", "暂停账户"],
	"orgs.resume": ["Resume the account", "恢复账户"],
	"orgs.pauseHint": ["Stops every brand's spending. Queued work stays queued.", "停止所有品牌的开销，已排队任务保留。"],
	"orgs.grant": ["Grant credits", "赠送积分"],
	"orgs.grantHint": ["Written to the ledger under the same lock a purchase uses.", "写入账本，与购买走同一把锁。"],
	"orgs.grantAmount": ["Credits", "积分数"],
	"orgs.grantExpires": ["Expires", "到期时间"],
	"orgs.grantExpiresHint": ["Leave empty for credits that never lapse.", "留空表示永不过期。"],
	"orgs.granted": ["Granted. Balance is now {balance}.", "已赠送，当前余额 {balance}。"],
	"orgs.revoke": ["Revoke credits", "扣回积分"],
	"orgs.revokeHint": [
		"Refused if the balance is short; an account is never pushed negative.",
		"余额不足时拒绝，账户不会变成负数。",
	],
	"orgs.revokeConfirm": [
		"Revoke {credits} credits from {name}? The ledger keeps the row: a mistake is corrected with a grant, not undone.",
		"从 {name} 扣回 {credits} 积分？账本会保留这条记录：弄错了只能再赠送，不能撤销。",
	],
	"orgs.revoked": ["Revoked. Balance is now {balance}.", "已扣回，当前余额 {balance}。"],
	"orgs.comp": ["Set the membership level", "设置会员级别"],
	"orgs.compHint": [
		"A paid tier writes the same rows a paid checkout does, for the months given, and grants the month's credits; the free tier ends the current plan. An account paygate is billing is changed at paygate.",
		"付费档位会写入与付费购买相同的记录、持续指定月数并发放当月积分；免费档位会结束当前套餐。由 paygate 计费的账户请在 paygate 修改。",
	],
	"orgs.planSet": ["Now on {plan}.", "已设为 {plan}。"],
	"orgs.compMonths": ["Months", "月数"],
	"orgs.comped": [
		"On {plan} until {until}. {credits} credits granted.",
		"已开通 {plan}，至 {until}，赠送 {credits} 积分。",
	],
	"orgs.end": ["End the subscription", "结束订阅"],
	"orgs.endHint": [
		"Only for one made here. A paygate subscription is cancelled at paygate.",
		"仅限在此开通的订阅；paygate 订阅需在 paygate 取消。",
	],
	"orgs.endConfirm": [
		"End this account's subscription now? It becomes read-only.",
		"立即结束该账户的订阅？账户将变为只读。",
	],
	"orgs.ended": ["Subscription ended.", "订阅已结束。"],
	"orgs.settings": ["Account settings", "账户设置"],
	"orgs.kind": ["Kind", "类型"],
	"orgs.kind.personal": ["Personal", "个人"],
	"orgs.kind.agency": ["Agency", "代理机构"],
	"orgs.planHint": [
		"The limits the account is held to. Being paid is the subscription, below.",
		"账户所受的限额。是否付费看下面的订阅。",
	],
	"orgs.balance": ["Balance", "余额"],
	"orgs.permanent": ["{n} permanent", "{n} 永久"],
	"orgs.expiring": ["{n} lapse {date}", "{n} 于 {date} 到期"],
	"orgs.ledger": ["Ledger", "账本"],
	"orgs.ledgerReason": ["Reason", "原因"],
	"orgs.ledgerDelta": ["Change", "变动"],
	"orgs.ledgerAfter": ["After", "余额"],
	"orgs.ledgerRef": ["Reference", "关联记录"],
	"orgs.subscriptions": ["Subscriptions", "订阅记录"],
	"orgs.period": ["Period", "周期"],
	"orgs.paygateId": ["paygate id", "paygate 编号"],
	"orgs.pending": ["moving to {plan}", "即将转为 {plan}"],
	"orgs.usage": ["Model usage, 30 days", "30 天模型用量"],
	"orgs.jobs": ["Recent jobs", "最近任务"],
	"orgs.actions": ["Recent actions", "最近操作"],
	"orgs.role": ["Role", "角色"],
	"orgs.since": ["Since", "加入于"],
	"orgs.onboarded": ["Onboarded", "已完成引导"],
	"orgs.notOnboarded": ["Not onboarded", "未完成引导"],

	// -- entities
	"entities.title": ["Listings", "收录条目"],
	"entities.lede": ["Every product in the directory, claimed or not.", "目录里的每个产品，无论是否被认领。"],
	"entities.name": ["Listing", "条目"],
	"entities.domains": ["Domains", "域名"],
	"entities.owner": ["Claimed by", "认领方"],
	"entities.unclaimed": ["Unclaimed", "未认领"],
	"entities.datapoints": ["Datapoints", "数据点"],
	"entities.published": ["Published", "已发布"],
	"entities.source": ["Source", "来源"],
	"entities.filter.claimed": ["Claimed", "已认领"],
	"entities.filter.unclaimed": ["Unclaimed", "未认领"],
	"entities.status.draft": ["Draft", "草稿"],
	"entities.status.listed": ["Listed", "已收录"],
	"entities.status.hidden": ["Hidden", "已隐藏"],
	"entities.status.merged": ["Merged", "已合并"],
	"entities.edit": ["Edit the listing", "编辑条目"],
	"entities.website": ["Website", "官网"],
	"entities.categories": ["Categories", "类目"],
	"category.placeholder": ["Choose categories", "选择类目"],
	"category.search": ["Search categories", "搜索类目"],
	"category.empty": ["No category matches that", "没有匹配的类目"],
	"category.loading": ["Loading categories…", "正在加载类目…"],
	"category.back": ["Back", "返回上一级"],
	"category.whole": ["All of {name}", "选择整个「{name}」"],
	"category.full": ["Up to {max} categories. Remove one to add another.", "最多 {max} 个类目，先移除一个再添加。"],
	"entities.aliases": ["Aliases", "别名"],
	"entities.listHint": ["One per line.", "每行一个。"],
	"entities.tagline_en": ["Tagline (English)", "一句话介绍（英文）"],
	"entities.tagline_zh": ["Tagline (Chinese)", "一句话介绍（中文）"],
	"entities.paused": ["Tracking paused", "已暂停追踪"],
	"entities.release": ["Release the claim", "解除认领"],
	"entities.releaseHint": [
		"Takes the listing back from the account. Its private questions and unpublished drafts go; published pages stay.",
		"从该账户收回条目。私有问题集和未发布草稿会删除，已发布页面保留。",
	],
	"entities.releaseConfirm": ["Release this listing from {org}?", "解除 {org} 对该条目的认领？"],
	"entities.released": ["Released. {n} drafts dropped.", "已解除，删除了 {n} 份草稿。"],
	"entities.pages": ["Pages", "页面"],
	"entities.claims": ["Claims", "认领记录"],
	"entities.measurement": ["Measurement", "度量"],
	"entities.questionSets": ["{n} question sets", "{n} 个问题集"],
	"entities.citations30": ["{n} citations in 30 days", "30 天内 {n} 次引用"],
	"entities.lastCited": ["Last cited {when}", "最近引用 {when}"],
	"entities.datapointsTitle": ["Datapoints", "数据点"],
	"entities.datapointsHint": [
		"Read-only here. A datapoint without a source is the thing this product does not publish, and the owner's panel is where sources are entered.",
		"这里只读。没有来源的数据点是本产品拒绝发布的内容；来源在所有者的面板中填写。",
	],
	"entities.dpKey": ["Key", "键"],
	"entities.dpValue": ["Value", "值"],
	"entities.dpSource": ["Source", "来源"],
	"entities.openPublic": ["Open on the directory", "在目录站打开"],

	// -- submissions
	"submissions.title": ["Submissions", "收录申请"],
	"submissions.lede": [
		"People asking to be listed, or to have a listing corrected. Approving a listing request is the only way a public form creates a listing.",
		"请求收录或修正条目的申请。批准收录申请是公开表单创建条目的唯一途径。",
	],
	"submissions.kind": ["Kind", "类型"],
	"submissions.kind.listing": ["New listing", "新收录"],
	"submissions.kind.correction": ["Correction", "修正"],
	"submissions.kind.removal": ["Removal", "移除"],
	"submissions.status.pending": ["Pending", "待处理"],
	"submissions.status.approved": ["Approved", "已批准"],
	"submissions.status.rejected": ["Rejected", "已拒绝"],
	"submissions.status.duplicate": ["Duplicate", "重复"],
	"submissions.from": ["From", "提交人"],
	"submissions.category": ["Category", "分类"],
	"submissions.existing": ["About", "涉及条目"],
	"submissions.approve": ["Approve", "批准"],
	"submissions.reject": ["Reject", "拒绝"],
	"submissions.duplicate": ["Mark duplicate", "标记重复"],
	"submissions.rejectConfirm": [
		"Reject the submission for {name}? It is closed for good; the submitter would have to send it again.",
		"拒绝 {name} 的收录申请？申请将永久关闭，提交人需要重新提交。",
	],
	"submissions.duplicateConfirm": [
		"Mark the submission for {name} as a duplicate? It is closed for good and no listing is created.",
		"将 {name} 的收录申请标记为重复？申请将永久关闭，不会创建条目。",
	],
	"submissions.approved": ["Approved. Listing {id} created.", "已批准，创建了条目 {id}。"],
	"submissions.decided": ["Marked {status}.", "已标记为 {status}。"],

	// -- pages
	"pages.title": ["Pages", "页面"],
	"pages.lede": [
		"Generated pages across every account: what is waiting, what is live.",
		"所有账户的生成页面：待审核的、已上线的。",
	],
	"pages.page": ["Page", "页面"],
	"pages.kind": ["Kind", "类型"],
	"pages.owner": ["Account", "账户"],
	"pages.about": ["About", "涉及"],
	"pages.status.draft": ["Draft", "草稿"],
	"pages.status.review": ["In review", "待审核"],
	"pages.status.published": ["Published", "已发布"],
	"pages.status.unpublished": ["Unpublished", "已下线"],
	"pages.publish": ["Publish", "发布"],
	"pages.publishHint": [
		"Runs the same validation the panel's approve button runs. A page without a leading answer or without JSON-LD is refused.",
		"执行与面板批准按钮相同的校验。缺少首段答案或 JSON-LD 的页面会被拒绝。",
	],
	"pages.publishConfirm": [
		"Publish this page on the directory under {org}'s name?",
		"以 {org} 的名义在目录站发布该页面？",
	],
	"pages.published": ["Published.", "已发布。"],
	"pages.unpublish": ["Take down", "下线"],
	"pages.unpublishHint": [
		"For a correction or a takedown. Never for a lapsed plan.",
		"用于修正或下架，绝不因套餐过期而下线。",
	],
	"pages.unpublishReason": ["Reason", "原因"],
	"pages.unpublished": ["Taken down.", "已下线。"],
	"pages.blocks": ["Blocks", "内容块"],
	"pages.reviewState": ["Review state", "审核状态"],
	"pages.revisions": ["Revisions", "修订记录"],
	"pages.revision": ["Revision", "修订"],
	"pages.citations": ["{n} citations recorded", "记录了 {n} 次引用"],
	"pages.recipe": ["Recipe", "配方"],
	"pages.generatedBy": ["Generated by", "生成方式"],
	"pages.jsonld": ["JSON-LD", "JSON-LD"],
	"pages.openPublic": ["Open on the directory", "在目录站打开"],

	// -- jobs
	"jobs.title": ["Jobs", "任务队列"],
	"jobs.lede": ["What the worker is doing and what it gave up on.", "worker 正在做什么、放弃了什么。"],
	"jobs.job": ["Job", "任务"],
	"jobs.kind": ["Kind", "类型"],
	"jobs.attempts": ["Attempts", "尝试"],
	"jobs.error": ["Last error", "最近错误"],
	"jobs.runAfter": ["Due", "计划时间"],
	"jobs.lane": ["Lane", "通道"],
	"jobs.lockedBy": ["Worker", "工作进程"],
	"jobs.status.queued": ["Queued", "排队中"],
	"jobs.status.running": ["Running", "运行中"],
	"jobs.status.done": ["Done", "完成"],
	"jobs.status.failed": ["Failed", "失败"],
	"jobs.status.skipped": ["Skipped", "已跳过"],
	"jobs.status.cancelled": ["Cancelled", "已取消"],
	"jobs.retry": ["Retry", "重试"],
	"jobs.retried": ["Queued again.", "已重新排队。"],
	"jobs.cancel": ["Cancel", "取消"],
	"jobs.cancelConfirm": [
		"Cancel this queued job? It will not run, and its key stays taken, so the same work cannot be queued again.",
		"取消这个排队中的任务？它不会再运行，且键会保留，同一工作无法再次排队。",
	],
	"jobs.cancelled": ["Cancelled.", "已取消。"],
	"jobs.payload": ["Payload", "参数"],
	"jobs.result": ["Result", "结果"],
	"jobs.events": ["Log", "日志"],

	// -- plans
	"plans.title": ["Plans and prices", "套餐与定价"],
	"plans.lede": [
		"What is sold and what each action costs. The seed rewrites these on deploy, so a number changed here is a number to carry into seed.rs.",
		"卖什么、每个动作花多少。部署时 seed 会重写这些值，所以这里改过的数字要同步进 seed.rs。",
	],
	"plans.orgsOn": ["{n} accounts", "{n} 个账户"],
	"plans.credits": ["Credits / month", "每月积分"],
	"plans.entities": ["Brands", "品牌数"],
	"plans.engines": ["Engines", "引擎数"],
	"plans.seats": ["Seats", "席位"],
	"plans.interval": ["Sample every (days)", "采样间隔（天）"],
	"plans.replication": ["Replications", "重复次数"],
	"plans.cname": ["CNAME", "CNAME"],
	"plans.questions": ["Questions / brand", "每个品牌问题数"],
	"plans.pagesMonth": ["Pages / month", "每月生成页数"],
	"plans.trial": ["Trial month, once per account", "体验月，每账户一次"],
	"plans.workflows": [
		"Custom workflows per account (0 = unlimited; built-ins excluded)",
		"每账户自定义工作流（0 为不限，内置不计入）",
	],
	"plans.agent": ["Agent", "Agent"],
	"plans.advanced": ["Advanced readings", "高级读数"],
	"plans.reviewFirst": ["Review first N pages", "前 N 页需人工审核"],
	"plans.reviewAlways": ["Always review", "始终审核"],
	"plans.priceUsd": ["USD / month", "美元 / 月"],
	"plans.priceUsdYear": ["USD / year", "美元 / 年"],
	"plans.priceCny": ["CNY / month", "人民币 / 月"],
	"plans.priceCnyYear": ["CNY / year", "人民币 / 年"],
	"plans.priceIds": ["paygate price ids", "paygate 价格编号"],
	"plans.contactOnly": ["Contact only", "仅联系销售"],
	"plans.active": ["Sold", "在售"],
	"plans.position": ["Position", "排序"],
	"plans.nameEn": ["Name (English)", "名称（英文）"],
	"plans.nameZh": ["Name (Chinese)", "名称（中文）"],
	"plans.markup": ["Credit markup", "积分加价"],
	"plans.markupHint": [
		"What a customer is charged over the model provider's list price, in basis points: 10000 is list exactly. Every call in the product is priced this way — there are no per-action prices.",
		"在模型厂商标价之上向客户收取的倍率，单位为基点：10000 表示照标价收。产品里每一次调用都按这个算，没有按动作定的价格。",
	],
	"plans.markupTimes": ["× {n} of list price", "标价的 {n} 倍"],
	"plans.markupConfirm": [
		"Change the credit markup from × {from} to × {to} of list price? Every call across the product is priced by it from now on.",
		"将积分加价从标价的 {from} 倍改为 {to} 倍？此后产品中每一次调用都按此计价。",
	],
	"plans.packs": ["Credit packs", "积分包"],
	"plans.packKey": ["Key", "键"],
	"plans.packCredits": ["Credits", "积分"],
	"plans.newPack": ["Add a pack", "新增积分包"],
	"plans.perDollar": [
		"One dollar is {n} credits everywhere a price is quoted in both.",
		"凡是同时标价的地方，一美元都等于 {n} 积分。",
	],

	// -- catalogue
	"catalogue.title": ["Industries and categories", "行业与分类"],
	"catalogue.lede": [
		"How the directory is filed. Edited through the seed, read here.",
		"目录的归档方式。通过 seed 修改，这里只读。",
	],
	"catalogue.category": ["Category", "分类"],
	"catalogue.locales": ["Languages", "语言"],
	"catalogue.entities": ["Listings", "条目数"],
	"catalogue.slug": ["Slug", "标识符"],

	// -- audit
	"audit.title": ["Audit log", "操作日志"],
	"audit.lede": [
		"Everything the panel and this console wrote down about having changed something.",
		"面板和管理中心记录下的每一次变更。",
	],
	"audit.when": ["When", "时间"],
	"audit.who": ["Who", "操作者"],
	"audit.action": ["Action", "操作"],
	"audit.target": ["Target", "对象"],
	"audit.org": ["Account", "账户"],
	"audit.detail": ["Detail", "详情"],
	"audit.filter.console": ["Console only", "仅管理中心"],
	"audit.filter.panel": ["Panel only", "仅面板"],

	// -- the server's refusals. Every code and every gate reason it can emit.
	"errors.model_output_invalid": ["The model's answer could not be read", "模型的回答无法解析"],
	"errors.ledger_amount_not_positive": ["The amount must be positive", "金额必须为正数"],
	"errors.member_owner_not_invited": ["An owner cannot be invited", "所有者不能通过邀请加入"],
	"errors.member_owner_not_settable": ["The owner cannot be set from this screen", "所有者不能在此页面设置"],
	"errors.page_revision_stale": [
		"The article changed since you opened it; reload and try again",
		"文章在你打开后已被修改，请刷新后重试",
	],
	"errors.page_jsonld_missing": ["A page without structured data cannot be published", "缺少结构化数据的页面无法发布"],
	"errors.page_kind_unsupported": [
		"Structured data is not available for this kind of page",
		"此类页面暂不支持结构化数据",
	],
	"errors.page_entity_missing": ["A brand page must name its brand", "品牌页面必须指明对应品牌"],
	"errors.page_ranked_empty": ["A ranked page must list at least one item", "排行页面至少需要一个条目"],
	"errors.page_dataset_meta_missing": ["A dataset page must carry dataset metadata", "数据集页面必须包含数据集元数据"],
	"errors.page_jsonld_invalid": ["The page's structured data is not valid", "页面结构化数据无效"],
	"errors.page_jsonld_incomplete": [
		"The page's structured data is missing a required field",
		"页面结构化数据缺少必填字段",
	],
	"errors.not_found": ["Not found", "未找到"],
	"errors.bad_request": ["The request was not accepted", "请求未被接受"],
	"errors.brand_category_unknown": ["That category is not in the list", "该类目不在列表中"],
	"errors.brand_categories_too_many": ["A brand can have at most five categories", "一个品牌最多选 5 个类目"],
	"errors.unauthorized": ["Sign in to continue", "请先登录"],
	"errors.forbidden": ["Not allowed", "无权限"],
	"errors.forbidden.origin": ["Refused: the request came from another site", "已拒绝：请求来自其他站点"],
	"errors.conflict": ["That conflicts with the current state", "与当前状态冲突"],
	"errors.rate_limited": ["Too many requests. Wait a moment.", "请求过于频繁，请稍候。"],
	"errors.gated": ["The account may not spend right now", "该账户目前不能消费"],
	"errors.gated.credits": ["Not enough credits", "积分不足"],
	"errors.gated.agent_credit_limit": [
		"The next call exceeds this run’s credit ceiling",
		"下一次调用将超过本次运行的积分上限",
	],
	"errors.gated.factcheck_required": [
		"Review the current fact-check evidence before publishing.",
		"请检查当前事实证据后再发布。",
	],
	"errors.gated.agent_tool_unavailable": ["This tool is not available yet", "此工具能力暂未开放"],
	"errors.gated.agent_run_stopped": ["This agent run has stopped", "该智能体运行已停止"],
	"errors.gated.content_plan_stopped": ["The content plan or action has stopped", "内容计划或行动已停止"],
	"errors.gated.content_permission_changed": ["The requester no longer has editing access", "发起人已无编辑权限"],
	"errors.gated.org_paused": ["The account is paused", "账户已暂停"],
	"errors.gated.brand_paused": ["The brand is paused", "品牌已暂停"],
	"errors.gated.platform_balance": ["The platform gateway balance is empty", "平台网关余额为空"],
	"errors.gated.platform_cap": ["The platform's daily cap is reached", "已达平台每日上限"],
	"errors.gated.entity_quota": ["The plan covers fewer brands", "套餐覆盖的品牌数不足"],
	"errors.gated.subscription_required": ["The account has no paid plan", "该账户没有付费套餐"],
	"errors.gated.subscription_expired": ["The account's plan has lapsed", "该账户的套餐已过期"],
	"errors.gated.engine_quota": ["The plan covers fewer engines", "套餐覆盖的引擎数不足"],
	"errors.gated.seat_quota": ["Every seat on the plan is taken", "套餐席位已用完"],
	"errors.gated.plan_overflow": ["The account is bigger than that plan", "账户规模超出该套餐"],
	"errors.gated.question_quota": ["The brand tracks every question the plan covers", "品牌追踪的问题数已达套餐上限"],
	"errors.gated.page_quota": ["The plan's pages for this month are spent", "本月套餐可生成的页面已用完"],
	"errors.gated.trial_used": ["The account has already taken its trial month", "该账户已经用过体验月"],
	"errors.gated.workflow_quota": ["Custom workflow limit reached", "自定义工作流数量已达上限"],
	"errors.gated.agent_plan": ["The plan does not include the Agent", "该套餐不含 Agent"],
	"errors.gated.advanced_plan": ["The plan does not include the advanced readings", "该套餐不含高级读数"],
	"errors.gated.market_price": ["No market price for the model", "该模型没有市场价格"],
	"errors.gated.no_supply": ["No supply for the model", "该模型暂无供给"],
	"errors.gated.already_yours": ["Already claimed by this account", "已由该账户认领"],
	"errors.gated.claimed_by_another": ["Claimed by another account", "已被其他账户认领"],
	"errors.gated.not_listed": ["The listing is not listed", "条目未收录"],
	"errors.gated.no_domain_on_record": ["The listing has no domain on record", "条目没有登记域名"],
	"errors.gateway_notice": ["The gateway refused the call", "网关拒绝了调用"],
	"errors.upstream": ["An upstream service failed", "上游服务出错"],
	// A handler that stopped because somebody paused the task. It never answers
	// a request; the sentence exists because every code the server can raise has
	// one.
	"errors.paused": ["This task was paused", "该任务已暂停"],
	"errors.misconfigured": ["This deployment is missing configuration", "本次部署缺少配置"],
	"errors.internal": ["Something went wrong on the server", "服务器出错"],
	"errors.invalid_json": ["Not valid JSON: {detail}", "不是有效的 JSON：{detail}"],
	"errors.notFound.user": ["No such person", "没有这个用户"],
	"errors.notFound.org": ["No such account", "没有这个账户"],
	"errors.notFound.entity": ["No such listing", "没有这个条目"],
	"errors.notFound.page": ["No such page", "没有这个页面"],
	"errors.conflict.model_request_changed": [
		"This model request already has different input",
		"此模型请求已绑定其他输入",
	],
	"errors.conflict.model_request_unsettled": [
		"This model call needs accounting reconciliation before retrying",
		"此模型调用需先核对状态与费用，暂不能重试",
	],
	"errors.conflict.model_usage_exceeds_reservation": [
		"Reported usage exceeds the reserved limit and needs reconciliation",
		"返回用量超过预留上限，需要核对",
	],
	"errors.conflict.model_usage_unmeasured": ["The model did not return reliable usage", "模型未返回可靠用量"],
	"errors.notFound.model_call": ["Model call not found", "模型调用不存在"],
	"errors.notFound.agent": ["Agent not found", "智能体不存在"],
	"errors.notFound.agent_version": ["Version not found", "版本不存在"],
	"errors.notFound.agent_run": ["Run not found", "运行不存在"],
	"errors.notFound.agent_step": ["Step not found", "步骤不存在"],
	"errors.conflict.agent_brand_busy": ["This brand already has an active run", "此品牌已有进行中的运行"],
	"errors.conflict.agent_run_stopped": [
		"That job belongs to a run that has already finished",
		"这个任务属于一次已经结束的运行",
	],
	"errors.conflict.agent_request_changed": [
		"This request key already has different inputs",
		"此请求标识已绑定其他输入",
	],
	"errors.conflict.agent_run_closed": [
		"This run or waiting step can no longer accept input",
		"此运行或等待步骤已不能接受输入",
	],
	"errors.agent_name": ["Enter a name up to 160 characters", "请输入不超过 160 字符的名称"],
	"errors.agent_execution_unsupported": [
		"This workflow uses a node or setting that the Agent runtime does not support yet.",
		"此工作流使用了 Agent 运行引擎尚未支持的节点或配置。",
	],
	"errors.agent_definition_invalid": [
		"The definition is invalid; open the agent and check its blocks",
		"定义无效，请打开该智能体检查其节点",
	],
	"errors.agent_executor_unavailable": [
		"This version contains steps the current executor does not support yet",
		"此版本包含当前执行器尚未支持的步骤",
	],
	"errors.agent_run_input": ["The run settings are invalid", "运行设置无效"],
	"errors.agent_schedule_input": [
		"Choose a published version, valid interval and credit ceiling",
		"请选择正式版本、有效周期与积分上限",
	],
	"errors.agent_model_empty": ["The model returned an empty response", "模型返回空响应"],
	"errors.agent_template_native_unsupported": [
		"This legacy workflow uses a block that cannot yet be copied to Agent",
		"该旧工作流包含尚不能复制到 Agent 的节点",
	],
	"errors.notFound.agent_chunk": ["Document chunk not found", "文档分块不存在"],
	"errors.notFound.agent_document": ["Document not found", "文档不存在"],
	"errors.notFound.agent_folder": ["Folder not found", "文件夹不存在"],
	"errors.notFound.agent_knowledge": ["Knowledge base not found", "知识库不存在"],
	"errors.notFound.agent_tag": ["Knowledge tag not found", "知识库标签不存在"],
	"errors.notFound.agent_upload": ["Upload not found", "上传记录不存在"],
	"errors.conflict.agent_embeddings_pending": ["Vector indexing is still in progress", "向量索引仍在处理中"],
	"errors.conflict.agent_knowledge_operation_changed": [
		"This request was already used with different input",
		"该请求已用于其他输入",
	],
	"errors.conflict.agent_knowledge_tag_conflict": ["A tag with this name already exists", "同名标签已存在"],
	"errors.conflict.agent_upload_state": [
		"This upload cannot be changed in its current state",
		"当前上传状态不允许此操作",
	],
	"errors.forbidden.agent_knowledge": ["You cannot access this knowledge base", "无权访问该知识库"],
	"errors.forbidden.agent_upload": ["You cannot access this upload", "无权访问该上传记录"],
	"errors.agent_document_format": ["This document format is not supported", "不支持该文档格式"],
	"errors.agent_embeddings_dimension": ["The embedding dimensions do not match", "向量维度不匹配"],
	"errors.agent_embeddings_unconfigured": ["The embedding service has not been configured", "尚未配置向量模型服务"],
	"errors.agent_folder_input": ["Check the folder name and location", "请检查文件夹名称和位置"],
	"errors.agent_knowledge_capacity": ["The knowledge base has reached its indexing limit", "知识库已达到索引容量上限"],
	"errors.agent_knowledge_input": ["Check the knowledge base inputs", "请检查知识库输入"],
	"errors.agent_knowledge_search_mode": ["This search mode is not supported", "不支持该检索模式"],
	"errors.agent_model_refusal": ["The model service declined this request", "模型服务拒绝了本次请求"],
	"errors.agent_model_output": ["The model output did not match the required schema", "模型输出不符合要求的结构"],
	"errors.notFound.agent_schedule": ["This schedule is unavailable", "该定时计划不可用"],
	"errors.agent_batch_limit": ["The list exceeds the batch limit", "列表超过批量上限"],
	"errors.agent_child_depth": ["The child workflow exceeds the nesting limit", "子工作流超过嵌套上限"],
	"errors.agent_parameters": ["Fill in the required inputs using their declared types", "请按字段要求填写输入"],
	"errors.agent_wait_limit": ["The wait exceeds its configured limit", "等待时间超过配置上限"],
	"errors.agent_assignee": ["Choose a member with editing access to this brand", "请选择对此品牌有编辑权限的成员"],
	"errors.agent_binding": ["A required input value is unavailable", "所需输入值不可用"],
	"errors.agent_comparison": ["The condition values cannot be compared", "条件值无法比较"],
	"errors.notFound.operation": ["That operation is no longer available", "该操作已不存在"],
	"errors.conflict.operation_input_changed": [
		"Use a new request key when changing the input",
		"更改输入时请使用新的请求标识",
	],
	"errors.conflict.operation_skipped": [
		"This operation was skipped. Check its status before trying again.",
		"此操作已跳过，请检查状态后再重试。",
	],
	"errors.notFound.job": ["No such job", "没有这个任务"],
	"errors.notFound.plan": ["No such plan", "没有这个套餐"],
	"errors.notFound.price": ["No such price", "没有这个价格"],
	"errors.notFound.credit_pack": ["No such pack", "没有这个积分包"],
	"errors.notFound.submission": ["No such submission", "没有这个申请"],
	"errors.notFound.subscription": ["No live subscription", "没有生效中的订阅"],

	// -- the console's own refusals. The server sends a code, never a sentence,
	// so an operator reading Chinese is not handed English prose; i18n.test.ts
	// fails the build if a payload here ever goes back to being one.
	"errors.admin_bad_enum": ["That is not one of the accepted values", "不是可接受的取值之一"],
	"errors.admin_negative_amount": ["An amount cannot be negative", "数值不能为负"],
	"errors.admin_org_name": ["A name is 1 to 120 characters", "名称需为 1 至 120 个字符"],
	"errors.admin_grant_range": ["A grant is 1 to 10,000,000 credits", "赠送额度为 1 至 10,000,000 积分"],
	"errors.admin_expiry_past": ["The expiry has to be in the future", "到期时间必须晚于当前时间"],
	"errors.admin_revoke_positive": ["A revoke has to be positive", "扣回额度必须为正数"],
	"errors.admin_comp_months": ["A comped plan runs for 1 to 36 months", "赠送套餐时长为 1 至 36 个月"],
	"errors.admin_plan_retired": ["That plan is no longer sold", "该套餐已停售"],
	"errors.admin_replication_range": ["Replication is 0 to 5", "重复采样次数为 0 至 5"],
	"errors.admin_plan_name_languages": ["A plan needs a name in both languages", "套餐需要中英文两种名称"],
	"errors.admin_markup_range": ["A markup is 1 to 100000 basis points", "加价为 1 至 100000 个基点"],
	"errors.admin_pack_credits": ["A pack holds at least one credit", "积分包至少包含 1 积分"],
	"errors.admin_pack_key_required": ["A pack needs a key", "积分包需要一个标识"],
	"errors.admin_pack_key_shape": ["A key is lowercase letters, digits, - and _", "标识只能包含小写字母、数字、- 和 _"],
	"errors.admin_self_demote": ["You cannot remove your own operator access", "不能移除自己的管理员权限"],
	"errors.admin_bootstrap_operator": [
		"The bootstrap operator is set by ONDIT_ADMIN_EMAIL, not here",
		"引导管理员由 ONDIT_ADMIN_EMAIL 设定，不能在此更改",
	],
	"errors.admin_bootstrap_password": [
		"The bootstrap operator's password is set by ONDIT_ADMIN_PASSWORD",
		"引导管理员的密码由 ONDIT_ADMIN_PASSWORD 设定",
	],
	"errors.admin_merge_by_importer": [
		"Merging is done by the importer, not by hand",
		"合并由导入流程完成，不能手动设置",
	],
	"errors.admin_entity_name": ["A name is 1 to 120 characters", "名称需为 1 至 120 个字符"],
	"errors.admin_entity_website": ["That is not a usable website URL", "不是可用的网站地址"],
	"errors.admin_bad_domain": ["That is not a domain", "不是有效的域名"],
	"errors.admin_submission_website": ["The submission has no usable website", "该申请没有可用的网站地址"],
	"errors.admin_submission_name": ["The submission has no name", "该申请没有名称"],
	"errors.conflict.paygate_plan": [
		"This account is billed by paygate; change the plan there and the webhook moves it here",
		"该账户由 paygate 计费，请在那里更改套餐，回调会同步到这里",
	],
	"errors.conflict.paygate_subscription": [
		"This subscription is billed by paygate; cancel it there and the webhook ends it here",
		"该订阅由 paygate 计费，请在那里取消，回调会同步到这里",
	],
	"errors.conflict.pack_key_taken": ["A pack with that key already exists", "已存在使用该标识的积分包"],
	"errors.conflict.listing_not_claimed": ["This listing is not claimed", "该条目未被认领"],
	"errors.conflict.page_already_published": ["This page is already published", "该页面已发布"],
	"errors.conflict.page_not_published": ["This page is not published", "该页面未发布"],
	"errors.conflict.submission_settled": ["This submission has already been decided", "该申请已处理过"],
	"errors.conflict.domain_already_listed": [
		"That domain is already listed; mark this a duplicate",
		"该域名已被收录，请标记为重复",
	],
	"errors.conflict.job_not_retriable": ["Only a failed or skipped job can be retried", "只有失败或跳过的任务可以重试"],
	"errors.conflict.job_not_queued": ["Only a queued job can be cancelled", "只有排队中的任务可以取消"],

	// -- ledger reasons, as the panel names them plus the console's own
	"ledger.subscription.start": ["Plan started", "订阅开始"],
	"ledger.subscription.renew": ["Monthly credits", "本月积分"],
	"ledger.subscription.canceled": ["Plan cancelled", "订阅已取消"],
	"ledger.sample_question": ["Sampling", "采样"],
	"ledger.page_generate": ["Page generated", "生成页面"],
	"ledger.page_generate_deep": ["Page generated (better models)", "生成页面（更强模型）"],
	"ledger.answer_rewrite": ["Answer rewritten", "答案改写"],
	"ledger.faq_clarify": ["FAQ clarified", "FAQ 澄清"],
	"ledger.reflow": ["Reflow report", "回流报告"],
	"ledger.generate.failed": ["Generation failed — refunded", "生成失败，已退回"],
	"ledger.opportunities_report": ["Opportunities report", "机会报告"],
	"ledger.opportunities.failed": ["Report failed — refunded", "报告失败，已退回"],
	"ledger.agent_turn": ["Assistant", "助手对话"],
	"ledger.seed": ["Starting balance", "初始额度"],
	"ledger.migration": ["Balance carried over", "结转余额"],
	"ledger.credit_pack": ["Credits bought", "充值积分"],
	"ledger.credits.expired": ["Plan credits expired", "套餐积分到期"],
	"ledger.admin.grant": ["Granted by an operator", "管理员赠送"],
	"ledger.admin.revoke": ["Revoked by an operator", "管理员扣回"],
	"ledger.usage": ["Model usage", "模型用量"],

	// -- subscription statuses, the CHECK on subscriptions.status
	"billing.status.active": ["Active", "生效中"],
	"billing.status.past_due": ["Past due", "逾期"],
	"billing.status.paused": ["Paused", "已暂停"],
	"billing.status.canceled": ["Cancelled", "已取消"],
};

export function translate(locale: Locale, key: string, vars?: Vars): string {
	const entry = STRINGS[key];
	if (!entry) return key;
	let text = entry[locale === "zh" ? 1 : 0];
	if (vars) {
		for (const [name, value] of Object.entries(vars)) {
			text = text.replaceAll(`{${name}}`, String(value));
		}
	}
	return text;
}

export function readLocale(): Locale {
	if (typeof document === "undefined") return "en";
	const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${LOCALE_COOKIE}=([^;]+)`));
	const raw = match ? decodeURIComponent(match[1]).toLowerCase() : "";
	if (raw.startsWith("zh")) return "zh";
	if (raw.startsWith("en")) return "en";
	return navigator.language.toLowerCase().startsWith("zh") ? "zh" : "en";
}

export function writeLocale(locale: Locale): void {
	const host = window.location.hostname;
	const domain = host.endsWith(".ondit.ai") || host === "ondit.ai" ? "; domain=.ondit.ai" : "";
	const secure = window.location.protocol === "https:" ? "; Secure" : "";
	// biome-ignore lint/suspicious/noDocumentCookie: parent-domain cookie, shared with the site and the panel
	document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=31536000; SameSite=Lax${domain}${secure}`;
}

interface I18n {
	locale: Locale;
	setLocale: (next: Locale) => void;
	t: (key: string, vars?: Vars) => string;
}

const Context = createContext<I18n | null>(null);

export function I18nProvider({
	locale,
	setLocale,
	children,
}: {
	locale: Locale;
	setLocale: (next: Locale) => void;
	children: ReactNode;
}) {
	const value = useMemo<I18n>(
		() => ({ locale, setLocale, t: (key, vars) => translate(locale, key, vars) }),
		[locale, setLocale],
	);
	return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useI18n(): I18n {
	const value = useContext(Context);
	if (!value) throw new Error("useI18n outside I18nProvider");
	return value;
}

export function useT(): I18n["t"] {
	return useI18n().t;
}
